import { useState, useEffect, useRef } from 'react';

export interface ProgressStep {
  id: string;
  message: string;
  status: 'completed' | 'active';
}

const DEFAULT_STEP_INTERVAL_MS = 2500;

/**
 * Simulates step-based progress while waiting for an async operation.
 * Advances through steps on a timer. Call start() when loading begins, stop() when done.
 */
export function useSimulatedSteps(
  stepMessages: readonly string[],
  intervalMs: number = DEFAULT_STEP_INTERVAL_MS
) {
  const [steps, setSteps] = useState<ProgressStep[]>([]);
  const [isActive, setIsActive] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stepIndexRef = useRef(0);

  const start = () => {
    stepIndexRef.current = 0;
    setSteps([
      { id: '1', message: stepMessages[0], status: 'active' },
    ]);
    setIsActive(true);
  };

  const stop = () => {
    setIsActive(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!isActive || stepMessages.length === 0) return;

    timerRef.current = setInterval(() => {
      stepIndexRef.current += 1;
      const idx = stepIndexRef.current;

      if (idx >= stepMessages.length) {
        // Stay on last step, keep it active
        setSteps((prev) =>
          prev.map((s, i) =>
            i < prev.length - 1
              ? { ...s, status: 'completed' as const }
              : { ...s, status: 'active' as const }
          )
        );
        return;
      }

      setSteps((prev) => {
        const completed = prev.map((s) =>
          s.status === 'active' ? { ...s, status: 'completed' as const } : s
        );
        return [
          ...completed,
          { id: String(idx + 1), message: stepMessages[idx], status: 'active' as const },
        ];
      });
    }, intervalMs);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isActive, stepMessages, intervalMs]);

  return { steps, start, stop };
}
